<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TaskForm from 'dashboard/components-next/Tasks/TaskForm.vue';
import ConfirmTaskDeleteDialog from 'dashboard/components-next/Tasks/ConfirmTaskDeleteDialog.vue';
import TaskRecurrenceScopeDialog from 'dashboard/components-next/Tasks/TaskRecurrenceScopeDialog.vue';

const props = defineProps({
  task: { type: Object, required: true },
  isUpdating: { type: Boolean, default: false },
  isDeleting: { type: Boolean, default: false },
});

const emit = defineEmits(['toggleStatus', 'updateTask', 'deleteTask']);

const { t } = useI18n();

const taskFormRef = ref(null);
const confirmDeleteDialogRef = ref(null);
const scopeDialogRef = ref(null);

const isCompleted = computed(() => props.task.status === 'completed');

const handleSave = async () => {
  if (!taskFormRef.value?.validate()) return;
  let scope;
  if (props.task.recurrenceRule) {
    scope = await scopeDialogRef.value?.ask('edit');
    if (!scope) return;
  }
  const updated = await taskFormRef.value?.save({
    scope,
    occurrenceDate: props.task.occurrenceDate,
  });
  if (updated) emit('updateTask', updated);
};

const openConfirmDeleteDialog = async () => {
  if (!props.task.recurrenceRule) {
    confirmDeleteDialogRef.value?.open();
    return;
  }
  const scope = await scopeDialogRef.value?.ask('delete');
  if (scope) {
    emit('deleteTask', props.task.id, {
      scope,
      occurrenceDate: props.task.occurrenceDate,
    });
  }
};

const handleConfirmDelete = () => {
  emit('deleteTask', props.task.id);
};
</script>

<template>
  <div>
    <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
      <TaskForm ref="taskFormRef" :task-data="task" />
      <div>
        <Button
          :label="t('TASKS.LIST.SAVE')"
          size="sm"
          :is-loading="isUpdating"
          :disabled="isUpdating"
          @click="handleSave"
        />
      </div>
    </div>

    <div
      class="flex items-center justify-between border-t border-n-strong px-6 py-5"
    >
      <Button
        :label="
          isCompleted ? t('TASKS.ACTIONS.REOPEN') : t('TASKS.ACTIONS.COMPLETE')
        "
        :variant="isCompleted ? 'faded' : 'solid'"
        :color="isCompleted ? 'slate' : 'teal'"
        size="sm"
        @click="emit('toggleStatus')"
      />
      <span class="inline-flex text-n-slate-11 text-sm items-center gap-1">
        {{ t('TASKS.LIST.DELETE.SECTION_DESCRIPTION') }}
        <Button
          :label="t('TASKS.LIST.DELETE.CONFIRM')"
          sm
          ruby
          link
          @click="openConfirmDeleteDialog"
        />
      </span>
    </div>

    <TaskRecurrenceScopeDialog ref="scopeDialogRef" />

    <ConfirmTaskDeleteDialog
      ref="confirmDeleteDialogRef"
      :task="{ id: task.id, title: task.title }"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
    />
  </div>
</template>
